import { useState, useEffect, useMemo, useRef } from 'react';
import { photoUrl } from './photoUtils';
import AutocompleteInput from './AutocompleteInput';
import HistorySuggestInput from './HistorySuggestInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Icon from './Icons';
import { fieldOptionList, useFieldOptions } from './fieldOptions';
import { useGrowingMethods, SubstratePicker, formatSubstrate } from './GrowingMethodPicker';
import { formatStatus, useStatusMeta } from './statuses';
import { plantDisplayName } from './nameUtils';

const DEFAULT_NOMENCLATURE = {
  baseGenus: '',
  baseSpecies: '',
  isHybrid: false,
  isIntergeneric: false,
  hasCultivar: false,
  hasInfraspecific: false,
  hasTradeName: false,
  speciesUnknown: false,
  infraspecificRank: 'var.',
  infraspecificName: '',
  fatherSpecies: '',
  fatherGenus: '',
  cultivar: ''
};

function normalizeHybridMarker(value) {
  return String(value || '').trim().replace(/\s+[xX]\s+/g, ' × ');
}

function parseIntergenericGenus(genus) {
  const clean = normalizeHybridMarker(genus);
  if (clean.includes(' × ')) {
    const parts = clean.split(' × ');
    return {
      baseGenus: parts[0]?.trim() || '',
      fatherGenus: parts.slice(1).join(' × ').trim(),
      isIntergeneric: true,
    };
  }
  if (clean.startsWith('×')) {
    return {
      baseGenus: clean.replace(/^×\s*/, '').trim(),
      fatherGenus: '',
      isIntergeneric: true,
    };
  }
  return {
    baseGenus: clean,
    fatherGenus: '',
    isIntergeneric: false,
  };
}

function parseSpeciesParts(species, taxonomyConfidence) {
  let baseSpecies = normalizeHybridMarker(species);
  let cultivar = '';
  let infraspecificRank = 'var.';
  let infraspecificName = '';
  let fatherSpecies = '';

  const cultivarMatch = baseSpecies.match(/\s*'([^']+)'\s*$/);
  if (cultivarMatch) {
    cultivar = cultivarMatch[1].trim();
    baseSpecies = baseSpecies.slice(0, cultivarMatch.index).trim();
  }

  const rankMatch = baseSpecies.match(/\s+(subsp\.|ssp\.|var\.|f\.|forma)\s+(.+)$/i);
  if (rankMatch) {
    const rawRank = rankMatch[1].toLowerCase();
    infraspecificRank = rawRank === 'ssp.' ? 'subsp.' : rawRank === 'forma' ? 'f.' : rawRank;
    infraspecificName = rankMatch[2].trim();
    baseSpecies = baseSpecies.slice(0, rankMatch.index).trim();
  }

  const isHybrid = baseSpecies.includes(' × ');
  if (isHybrid) {
    const parts = baseSpecies.split(' × ');
    baseSpecies = parts[0]?.trim() || '';
    fatherSpecies = parts.slice(1).join(' × ').trim();
  }

  const speciesUnknown =
    baseSpecies === '' ||
    ['sp', 'sp.', 'spp', 'spp.', 'unknown', 'unk'].includes(baseSpecies.toLowerCase()) ||
    taxonomyConfidence === 'unknown';

  return {
    baseSpecies: speciesUnknown ? '' : baseSpecies,
    speciesUnknown,
    isHybrid,
    fatherSpecies,
    hasCultivar: Boolean(cultivar),
    cultivar,
    hasInfraspecific: Boolean(infraspecificName),
    infraspecificRank,
    infraspecificName,
  };
}

export function parseNomenclatureIdentity({
  genus = '',
  species = '',
  trade_name = '',
  taxonomy_confidence = '',
} = {}) {
  const parsedGenus = parseIntergenericGenus(genus);
  const parsedSpecies = parseSpeciesParts(species, taxonomy_confidence);

  return {
    ...DEFAULT_NOMENCLATURE,
    ...parsedGenus,
    ...parsedSpecies,
    hasTradeName: Boolean(trade_name),
  };
}

function hasAdvancedNomenclature(nomenclature) {
  return Boolean(
    nomenclature.isHybrid ||
    nomenclature.isIntergeneric ||
    nomenclature.hasCultivar ||
    nomenclature.hasInfraspecific ||
    nomenclature.hasTradeName ||
    nomenclature.speciesUnknown
  );
}

function hasIdentityPrefill(prefill) {
  if (!prefill) return false;
  return ['genus', 'species', 'trade_name', 'taxonomy_confidence'].some(key =>
    String(prefill[key] || '').trim()
  );
}

function PlantForm({ wishlistMode = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditing = Boolean(id);
  const { statuses: statusMetadata, byKey: statusByKey } = useStatusMeta();

  // Pre-filled data and photo from the photo ID flow
  const rawPrefill = location.state?.prefill;
  const prefill = rawPrefill || {};
  const swapDispositionId = location.state?.swap_disposition_id;
  const swapSourcePlantId = location.state?.swap_source_plant_id;
  const swapSourceSpecimenId = location.state?.swap_source_specimen_id;
  const prefillNomenclature = useMemo(
    () => hasIdentityPrefill(rawPrefill)
      ? parseNomenclatureIdentity(rawPrefill)
      : { ...DEFAULT_NOMENCLATURE },
    [rawPrefill]
  );
  const duplicateConfirmed = location.state?.duplicateConfirmed || false;
  const fromAddFlow = location.state?.fromAddFlow || false;
  const prefillStatus = (() => {
    const raw = location.state?.prefillStatus;
    return (raw && statusByKey[raw]) ? raw : null;
  })();

  const [plant, setPlant] = useState(null);
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [lightSources, setLightSources] = useState([]);
  const [statusDetails, setStatusDetails] = useState({
    date: '',
    amount: '',
    counterparty_name: '',
    counterparty_contact: '',
    received_plant_id: '',
    received_description: '',
    person_name: '',
    person_contact: '',
    expected_return_at: '',
    notes: '',
  });
  const [swapSearch, setSwapSearch] = useState('');
  const [swapMatches, setSwapMatches] = useState([]);
  const [selectedSwapPlant, setSelectedSwapPlant] = useState(null);
  const growingMethods = useGrowingMethods();
  const fieldOptions = useFieldOptions();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formDuplicates, setFormDuplicates] = useState(null);
  const [expandedDuplicate, setExpandedDuplicate] = useState(null);
  const [showSpecimenInfo, setShowSpecimenInfo] = useState(false);
  const [showGrowingMethodInfo, setShowGrowingMethodInfo] = useState(false);
  const [confirmingIdChange, setConfirmingIdChange] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // --- New Nomenclature & UI State ---
  const [showNamingOptions, setShowNamingOptions] = useState(false);
  const [suggestedCommonNames, setSuggestedCommonNames] = useState([]);
  const [nomenclature, setNomenclature] = useState(prefillNomenclature);

  useEffect(() => {
    if (!isEditing) setShowNamingOptions(hasAdvancedNomenclature(prefillNomenclature));
  }, [isEditing, location.pathname, prefillNomenclature]);

  const handleNomenclatureChange = (changes) => {
    setNomenclature(prev => ({ ...prev, ...changes }));
  };

  const buildFinalNames = () => {
    const n = nomenclature;
    
    // Genus: base, or intergeneric hybrid format
    let genus = n.baseGenus.trim();
    if (n.isIntergeneric) {
      genus = n.fatherGenus.trim()
        ? `${genus} × ${n.fatherGenus.trim()}`
        : `× ${genus}`;
    }

    // Species: base → hybrid → infraspecific → cultivar
    let species = n.speciesUnknown ? 'sp.' : n.baseSpecies.trim();
    if (n.isHybrid && n.fatherSpecies.trim()) {
      species += ` × ${n.fatherSpecies.trim()}`;
    }
    if (n.hasInfraspecific && n.infraspecificName.trim()) {
      species += ` ${n.infraspecificRank} ${n.infraspecificName.trim()}`;
    }
    if (n.hasCultivar && n.cultivar.trim()) {
      species += ` '${n.cultivar.trim()}'`;
    }

    return { genus, species };
  };

  const getAutoIdBasisValue = () => {
    const n = nomenclature;
    if (n.isHybrid || n.isIntergeneric) return 'hybrid_formula';
    if (n.baseSpecies.trim() && !n.speciesUnknown) return 'species';
    // Cultivar present without a known base species (e.g. sp. 'Thai Constellation').
    // Use 'species' so the backend derives the identity code from the cultivar name
    // rather than falling through to genus_unknown → SPC. The backend's
    // _identity_code_from_label strips 'sp.' and uses the cultivar words.
    if (n.hasCultivar && n.cultivar.trim()) return 'species';
    if (n.hasTradeName && form.trade_name.trim()) return 'trade_name';
    return 'genus_unknown';
  };

  const getIdBasisLabel = (basis) => {
    const labels = {
      hybrid_formula: 'Hybrid Formula',
      species: 'Species',
      trade_name: 'Trade Name',
      genus_unknown: 'Genus Unresolved',
    };
    return labels[basis] || 'Auto';
  };

  const canUseTradeNameForId = () => {
    return Boolean(nomenclature.hasTradeName && form.trade_name.trim() && getAutoIdBasisValue() !== 'trade_name');
  };

  const getResolvedIdBasisValue = () => {
    return form.id_basis === 'trade_name' && canUseTradeNameForId()
      ? 'trade_name'
      : getAutoIdBasisValue();
  };

  const codeFromLabel = (label, fallback = 'XXX') => {
    const words = String(label || '')
      .replace(/[^A-Za-z0-9 ×'-]+/g, ' ')
      .replace(/\b(cf|aff|sp|spp|unknown|unk)\.?\b/gi, ' ')
      .replace(/'/g, ' ')
      .split(/\s+/)
      .filter(word => word && word !== '×');

    if (words.length === 0) return fallback;
    if (words.length >= 2) {
      const last = words[words.length - 1];
      const code = last.length <= 4
        ? `${words[0][0]}${last[0]}${last[last.length - 1]}`
        : `${words[0][0]}${last.slice(0, 2)}`;
      return code.toUpperCase().padEnd(3, 'X').slice(0, 3);
    }
    return words[0].slice(0, 3).toUpperCase().padEnd(3, 'X').slice(0, 3);
  };

  const genusCode = (genus) => {
    const clean = String(genus || '').trim();
    if (!clean) return 'GGG';
    if (clean.includes(' × ')) {
      const parts = clean.split(' × ');
      return `${codeFromLabel(parts[0], 'GGG')}×${codeFromLabel(parts[1], 'GGG')}`;
    }
    if (clean.startsWith('×')) {
      return `X${clean.replace('×', '').trim().slice(0, 2).toUpperCase().padEnd(2, 'X')}`;
    }
    return clean.slice(0, 3).toUpperCase().padEnd(3, 'X');
  };

  const identityCode = () => {
    const { species } = buildFinalNames();
    const basis = getResolvedIdBasisValue();
    if (basis === 'trade_name') return codeFromLabel(form.trade_name, 'XXX');
    if (basis === 'genus_unknown') return 'SPC';
    if (species.includes(' × ')) {
      const parts = species.split(' × ');
      return `${codeFromLabel(parts[0], 'XXX')}×${codeFromLabel(parts[1], 'XXX')}`;
    }
    return codeFromLabel(species, 'XXX');
  };

  const specimenIdPreview = () => {
    const manualId = form.specimen_id.trim();
    if (manualId) {
      return { value: manualId.toUpperCase(), mode: 'Manual override' };
    }
    const { genus } = buildFinalNames();
    if (!genus.trim()) {
      return { value: '', mode: '' };
    }
    const basis = getResolvedIdBasisValue();
    const hasSpecies   = !nomenclature.speciesUnknown && Boolean(nomenclature.baseSpecies.trim());
    const hasTradeName = nomenclature.hasTradeName && Boolean(form.trade_name.trim());
    const hasCultivar  = nomenclature.hasCultivar && Boolean(nomenclature.cultivar.trim());
    // basis='genus_unknown' with speciesUnknown shows ALO-SPC-###
    // basis='species' needs at least one of: known species, cultivar, trade name
    const hasKnownIdentity = basis === 'genus_unknown'
      ? nomenclature.speciesUnknown
      : (hasSpecies || hasTradeName || hasCultivar);
    if (!hasKnownIdentity) {
      return { value: '', mode: '' };
    }
    return { value: `${genusCode(genus)}-${identityCode()}-###`, mode: getIdBasisLabel(basis) };
  };

  const [form, setForm] = useState({
    common_name: prefill.common_name || '',
    specimen_id: prefill.specimen_id || '',
    trade_name: prefill.trade_name || '',
    id_basis: prefill.id_basis || '',
    taxonomy_confidence: prefill.taxonomy_confidence || '',
    status: wishlistMode ? 'wishlist' : (prefill.status || 'collection'),
    loss_reason: prefill.loss_reason || '',
    acquisition_date: prefill.acquisition_date || '',
    source: prefill.source || '',
    cost: prefill.cost != null ? String(prefill.cost) : '',
    target_price: prefill.target_price != null ? String(prefill.target_price) : '',
    wishlist_priority: prefill.wishlist_priority != null ? String(prefill.wishlist_priority) : '',
    wishlist_url: prefill.wishlist_url || '',
    shop_name: prefill.shop_name || '',
    acquisition_notes: prefill.acquisition_notes || '',
    growing_method: prefill.growing_method || '',
    substrate: prefill.substrate || '',
    moss_pole_type: prefill.moss_pole_type || '',
    cabinet_id: prefill.cabinet_id || '',
    notes: prefill.notes || '',
    location_ids: [],
    tag_ids: [],
    light_source_ids: [],
  });

  const isWishlistForm = wishlistMode || form.status === 'wishlist';

  // -- Specimen ID preview (backend-authoritative) ----------------------------
  // Calls /plants/specimen-id-preview with a 350ms debounce so the preview
  // always matches what build_specimen_prefix() will actually produce.
  // The local specimenIdPreview() function is kept as a fast synchronous
  // fallback for instant feedback while the fetch is in-flight.
  const [specimenPreview, setSpecimenPreview] = useState({ value: '', mode: '' });
  const previewAbortRef = useRef(null);

  useEffect(() => {
    const manualId = form.specimen_id?.trim();
    if (manualId) {
      setSpecimenPreview({ value: manualId.toUpperCase(), mode: 'Manual override' });
      return;
    }
    const { genus, species } = buildFinalNames();
    if (!genus.trim()) { setSpecimenPreview({ value: '', mode: '' }); return; }

    const basis = getResolvedIdBasisValue();
    const hasSpecies  = !nomenclature.speciesUnknown && Boolean(nomenclature.baseSpecies.trim());
    const hasTradeName = nomenclature.hasTradeName && Boolean(form.trade_name.trim());
    const hasCultivar  = nomenclature.hasCultivar && Boolean(nomenclature.cultivar.trim());
    const hasKnownIdentity = basis === 'genus_unknown'
      ? nomenclature.speciesUnknown
      : (hasSpecies || hasTradeName || hasCultivar);
    if (!hasKnownIdentity) { setSpecimenPreview({ value: '', mode: '' }); return; }

    // Optimistic local preview (fast path) while backend call is in-flight
    setSpecimenPreview(specimenIdPreview());

    if (previewAbortRef.current) previewAbortRef.current.abort();
    previewAbortRef.current = new AbortController();
    const { signal } = previewAbortRef.current;

    const params = new URLSearchParams({ genus, species });
    if (form.trade_name) params.set('trade_name', form.trade_name);
    if (basis) params.set('id_basis', basis);

    const timer = setTimeout(() => {
      fetch(`/plants/specimen-id-preview?${params}`, { signal })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) setSpecimenPreview({ value: data.preview_id, mode: data.mode });
        })
        .catch(() => {}); // silent on abort or network error; local preview remains
    }, 350);
    return () => {
      clearTimeout(timer);
      // Abort any in-flight request so stale responses can't update state
      // after a dependency change or component unmount.
      previewAbortRef.current?.abort();
      previewAbortRef.current = null;
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    nomenclature, form.trade_name, form.id_basis, form.specimen_id,
  ]);

  useEffect(() => {
    const fetches = [
      fetch('/locations').then(r => r.json()),
      fetch('/tags').then(r => r.json()),
      fetch('/light-sources').then(r => r.json()),
    ];

    // If editing, also fetch the existing plant
    if (isEditing) {
      fetches.push(
        fetch(`/plants/${id}`).then(r => {
          if (!r.ok) throw new Error('Plant not found');
          return r.json();
        })
      );
    }

    Promise.all(fetches)
      .then(([locData, tagData, lsData, plantData]) => {
        setLocations(locData);
        setTags(tagData);
        setLightSources(lsData);

        // Pre-fill form with existing plant data
        if (plantData) {
          setPlant(plantData);

          // fromAddFlow + stub: the plant is a placeholder (Unknown/unknown) created by
          // handleSkipWithPhoto. Reset nomenclature so the user gets a blank form; the
          // stub genus/species and its auto-generated specimen ID must not pre-fill.
          // A successful PlantNet suggestion also arrives with fromAddFlow but has real
          // taxonomy — detect the stub specifically by its sentinel genus/species values.
          const isManualStub = fromAddFlow &&
            plantData.genus === 'Unknown' && plantData.species === 'unknown';

          if (isManualStub) {
            setNomenclature({ ...DEFAULT_NOMENCLATURE });
            setShowNamingOptions(false);
          } else {
            const parsedNomenclature = parseNomenclatureIdentity(plantData);
            setNomenclature(parsedNomenclature);
            if (hasAdvancedNomenclature(parsedNomenclature)) {
              setShowNamingOptions(true);
            }
          }

          setForm({
            common_name: plantData.common_name || '',
            specimen_id: isManualStub ? '' : (plantData.specimen_id || ''),
            trade_name: plantData.trade_name || '',
            id_basis: plantData.id_basis || '',
            taxonomy_confidence: plantData.taxonomy_confidence || '',
            status: prefillStatus || plantData.status || 'collection',
            loss_reason: plantData.loss_reason || '',
            acquisition_date: plantData.acquisition_date || '',
            source: plantData.source || '',
            cost: plantData.cost != null ? String(plantData.cost) : '',
            target_price: plantData.target_price != null ? String(plantData.target_price) : '',
            wishlist_priority: plantData.wishlist_priority != null ? String(plantData.wishlist_priority) : '',
            wishlist_url: plantData.wishlist_url || '',
            shop_name: plantData.shop_name || '',
            acquisition_notes: plantData.acquisition_notes || '',
            growing_method: plantData.growing_method || '',
            substrate: plantData.substrate || '',
            moss_pole_type: plantData.moss_pole_type || '',
            cabinet_id: plantData.cabinet_id || '',
            notes: plantData.notes || '',
            location_ids: plantData.locations.map(l => l.id),
            tag_ids: plantData.tags.map(t => t.id),
            light_source_ids: plantData.light_sources.map(ls => ls.id),
          });

          if (prefillStatus && prefillStatus !== plantData.status) {
            setStatusDetails(prev => ({ ...prev, date: new Date().toISOString().slice(0, 10) }));
          }
        }

        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [id, isEditing, prefillStatus, fromAddFlow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'status' && value !== form.status && !statusDetails.date) {
      setStatusDetails(prev => ({ ...prev, date: new Date().toISOString().slice(0, 10) }));
    }
  };

  const handleStatusDetailChange = (e) => {
    const { name, value } = e.target;
    setStatusDetails(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (form.status !== 'swapped' || swapSearch.trim().length < 2) {
      setSwapMatches([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(`/plants?q=${encodeURIComponent(swapSearch.trim())}`, { signal: controller.signal })
        .then(r => r.ok ? r.json() : [])
        .then(results => {
          const currentId = id ? Number(id) : null;
          setSwapMatches(results.filter(p => p.id !== currentId));
        })
        .catch(err => {
          if (err.name !== 'AbortError') setSwapMatches([]);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.status, swapSearch, id]);

  const handleToggle = (field, toggleId) => {
    setForm(prev => {
      const current = prev[field];
      const updated = current.includes(toggleId)
        ? current.filter(i => i !== toggleId)
        : [...current, toggleId];
      return { ...prev, [field]: updated };
    });
  };

  const handleMoveSpeciesToCultivar = () => {
    const speciesVal = nomenclature.baseSpecies.trim();
    const match = speciesVal.match(/'([^']+)'/);
    if (!match) return;
    const cultivarName = match[1];
    const remainder = speciesVal.replace(/'[^']+'/, '').trim();
    const becomesUnknown = remainder === 'sp.' || remainder === '';
    handleNomenclatureChange({
      cultivar: cultivarName,
      hasCultivar: true,
      baseSpecies: becomesUnknown ? '' : remainder,
      speciesUnknown: becomesUnknown || nomenclature.speciesUnknown,
    });
    setShowNamingOptions(true);
  };

  const handleMoveSpeciesToTradeName = () => {
    const text = nomenclature.baseSpecies.trim();
    handleNomenclatureChange({ hasTradeName: true, speciesUnknown: true, baseSpecies: '' });
    setForm(prev => ({
      ...prev,
      trade_name: text,
      common_name: prev.common_name || text,
    }));
    setShowNamingOptions(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setFormDuplicates(null);

    if (!nomenclature.baseGenus.trim()) {
      setError('Genus is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!nomenclature.speciesUnknown && !nomenclature.baseSpecies.trim()) {
      setError('Species is required unless Species unknown / unresolved is selected in Additional Naming Options');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (nomenclature.isHybrid && !nomenclature.fatherSpecies.trim()) {
      setError('Please provide the Father species for this hybrid');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Manual ID override validation
    const manualId = form.specimen_id?.trim();
    const originalId = isEditing ? (plant?.specimen_id || '') : '';

    if (manualId && manualId !== originalId) {
      const idRegex = /^[A-Z0-9×]{3,}-[A-Z0-9'×]{3,10}-\d{3,}$/i;
      if (!idRegex.test(manualId)) {
        setError("Invalid ID Format. Use 'GEN-SPE-001'.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!confirmingIdChange) { setConfirmingIdChange(true); return; }
      setConfirmingIdChange(false);
    }

    // Duplicate check for new plants — skip when fromAddFlow since plant was already created
    if (!isEditing && !duplicateConfirmed && !formDuplicates && !fromAddFlow) {
      const { genus, species } = buildFinalNames();
      fetch(`/plants/check-duplicate?genus=${encodeURIComponent(genus)}&species=${encodeURIComponent(species)}`)
        .then(r => r.json())
        .then(matches => {
          if (matches.length > 0) {
            setFormDuplicates(matches);
            setTimeout(() => {
              window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 100);
          } else {
            submitPlant();
          }
        })
        .catch(() => submitPlant());
      return;
    }

    submitPlant();
  };

  const shouldCreateStatusRecord = (savedPlant) => {
    const statusChanged = !isEditing || form.status !== plant?.status;
    const hasDetails = Object.values(statusDetails).some(v => String(v || '').trim());
    return Boolean(savedPlant?.id) && statusChanged && (
      ['sold', 'gifted', 'swapped', 'deceased'].includes(form.status) ||
      ['loaned_in', 'loaned_out'].includes(form.status) ||
      hasDetails
    );
  };

  const createStatusRecord = (savedPlant) => {
    if (!shouldCreateStatusRecord(savedPlant)) return Promise.resolve();

    if (form.status === 'sold') {
      return fetch('/sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plant_id: savedPlant.id,
          date: statusDetails.date || null,
          price: statusDetails.amount ? parseFloat(statusDetails.amount) : null,
          buyer_name: statusDetails.counterparty_name.trim() || null,
          buyer_contact: statusDetails.counterparty_contact.trim() || null,
          notes: statusDetails.notes.trim() || null,
        }),
      }).then(async r => {
        if (!r.ok) throw new Error((await r.json()).detail || 'Plant saved, but sale details failed to save');
      });
    }

    if (['gifted', 'swapped', 'deceased'].includes(form.status)) {
      return fetch('/plant-dispositions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plant_id: savedPlant.id,
          type: form.status,
          date: statusDetails.date || null,
          counterparty_name: statusDetails.counterparty_name.trim() || null,
          counterparty_contact: statusDetails.counterparty_contact.trim() || null,
          received_plant_id: selectedSwapPlant?.id || null,
          received_description: form.status === 'swapped' ? (statusDetails.received_description.trim() || null) : null,
          loss_reason: form.status === 'deceased' ? (form.loss_reason || null) : null,
          notes: statusDetails.notes.trim() || null,
        }),
      }).then(async r => {
        if (!r.ok) throw new Error((await r.json()).detail || 'Plant saved, but disposition details failed to save');
      });
    }

    if (['loaned_in', 'loaned_out'].includes(form.status)) {
      return fetch('/plant-loans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plant_id: savedPlant.id,
          direction: form.status,
          person_name: statusDetails.person_name.trim() || statusDetails.counterparty_name.trim() || null,
          person_contact: statusDetails.person_contact.trim() || statusDetails.counterparty_contact.trim() || null,
          loaned_at: statusDetails.date || null,
          expected_return_at: statusDetails.expected_return_at || null,
          notes: statusDetails.notes.trim() || null,
        }),
      }).then(async r => {
        if (!r.ok) throw new Error((await r.json()).detail || 'Plant saved, but loan details failed to save');
      });
    }

    return Promise.resolve();
  };

  const linkReceivedSwapPlant = (savedPlant) => {
    if (!swapDispositionId || !savedPlant?.id) {
      return Promise.resolve({ savedPlant, swapLinkFailed: false });
    }
    return fetch(`/plant-dispositions/${swapDispositionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ received_plant_id: savedPlant.id }),
    }).then(async response => {
      if (!response.ok) {
        throw new Error((await response.json()).detail || 'Failed to link swap record');
      }
      return { savedPlant, swapLinkFailed: false };
    }).catch(() => ({ savedPlant, swapLinkFailed: true }));
  };

  const submitPlant = () => {
    setSaving(true);
    setError(null);

    const { genus, species } = buildFinalNames();

    // Auto-tag hybrids
    let updatedTagIds = [...form.tag_ids];
    if (nomenclature.isHybrid || nomenclature.isIntergeneric) {
      const hybridTag = tags.find(t => t.name.toLowerCase() === 'hybrid');
      if (hybridTag && !updatedTagIds.includes(hybridTag.id)) {
        updatedTagIds.push(hybridTag.id);
      }
    }

    const payload = {
      genus,
      species,
      specimen_id: (form.specimen_id?.trim() && form.specimen_id.trim() !== plant?.specimen_id)
        ? form.specimen_id.trim()
        : null,
      trade_name: nomenclature.hasTradeName ? (form.trade_name.trim() || null) : null,
      id_basis: getResolvedIdBasisValue(),
      common_name: form.common_name.trim() || null,
      status: form.status,
      loss_reason: form.status === 'deceased' ? (form.loss_reason || null) : null,
      acquisition_date: form.acquisition_date || null,
      source: form.source.trim() || null,
      cost: form.cost ? parseFloat(form.cost) : null,
      growing_method: form.growing_method || null,
      substrate: form.substrate.trim() || null,
      moss_pole_type: form.moss_pole_type || null,
      cabinet_id: form.cabinet_id.trim() || null,
      target_price: form.target_price ? parseFloat(form.target_price) : null,
      wishlist_priority: form.wishlist_priority ? parseInt(form.wishlist_priority, 10) : null,
      wishlist_url: form.wishlist_url.trim() || null,
      shop_name: form.shop_name.trim() || null,
      acquisition_notes: form.acquisition_notes.trim() || null,
      notes: form.notes.trim() || null,
      location_ids: form.location_ids,
      tag_ids: updatedTagIds,
      light_source_ids: form.light_source_ids,
    };

    if (nomenclature.speciesUnknown) {
      payload.taxonomy_confidence = 'unknown';
    }

    const url = isEditing ? `/plants/${id}` : '/plants';
    const method = isEditing ? 'PUT' : 'POST';

    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async response => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Failed to ${isEditing ? 'update' : 'create'} plant`);
        }
        return response.json();
      })
      .then(savedPlant => createStatusRecord(savedPlant).then(() => savedPlant))
      .then(savedPlant => linkReceivedSwapPlant(savedPlant))
      .then(({ savedPlant, swapLinkFailed }) => {
        // replace: edit form should not sit in the back stack after a successful save
        navigate(`/plants/${savedPlant.id}`, {
          replace: true,
          state: {
            saved: true,
            swapLinkFailed,
            swapSourcePlantId,
            swapSourceSpecimenId,
          },
        });
      })
      .catch(err => {
        setError(err.message);
        setSaving(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  const handleDelete = () => {
    if (!confirmingDelete) { setConfirmingDelete(true); return; }
    setConfirmingDelete(false);

    fetch(`/plants/${id}`, { method: 'DELETE' })
      .then(response => {
        if (!response.ok) throw new Error('Failed to delete plant');
        // replace: the deleted plant's pages are invalid history entries
        navigate('/', { replace: true });
      })
      .catch(err => setError(err.message));
  };

  const backLink = isEditing ? `/plants/${id}` : wishlistMode ? '/wishlist' : '/new';
  const backOpts = (isEditing || wishlistMode) ? { replace: true } : undefined;
  const backText = isEditing ? 'Back to Plant' : 'Back';

  if (loading) return (
    <div className="app">
      <header className="sub-header">
        <button type="button" className="sub-header-back" onClick={() => navigate(backLink, backOpts)} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>{isEditing ? 'Edit Plant' : 'Add Plant'}</h1>
        </div>
      </header>
      <p className="health-loading">Loading…</p>
    </div>
  );
  const preview = specimenPreview; // backend-authoritative via useEffect above
  const mossPoleOptions = fieldOptionList(fieldOptions, 'moss_pole_type');
  const lossReasonOptions = fieldOptionList(fieldOptions, 'loss_reason');
  const statusChanged = !isEditing || form.status !== plant?.status;
  const showDispositionPanel = statusChanged && ['sold', 'gifted', 'swapped', 'deceased'].includes(form.status);
  const showLoanPanel = statusChanged && ['loaned_in', 'loaned_out'].includes(form.status);

  const speciesVal = nomenclature.baseSpecies.trim();
  const speciesCultivarHint =
    !nomenclature.speciesUnknown &&
    /'[^']+'/.test(speciesVal);
  const speciesTradeNameHint =
    !nomenclature.speciesUnknown &&
    !speciesCultivarHint &&
    speciesVal.length > 0 &&
    /\b[A-Z][a-z]+/.test(speciesVal) &&
    !/^(sp|cf|aff)\b/i.test(speciesVal);
  const cnWords = form.common_name.trim().split(/\s+/).filter(Boolean);
  const commonNameTradeHint =
    suggestedCommonNames.length === 0 &&
    !nomenclature.hasTradeName &&
    cnWords.length >= 1 && cnWords.length <= 4 &&
    /^[A-Z]/.test(form.common_name.trim()) &&
    cnWords.every(w => /^[A-Z]/.test(w) || /^(of|the|de|du|van|von)$/i.test(w));

  return (
    <div className="app">
      <header className="sub-header">
        <button
          type="button"
          className="sub-header-back"
          onClick={() => navigate(backLink, backOpts)}
          aria-label={backText}
          title={backText}
        >
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>{isEditing ? 'Edit Plant' : 'Add Plant'}</h1>
          {isEditing && plant?.specimen_id && (
            <p className="sub-header-subtitle">{plant.specimen_id}</p>
          )}
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      <form onSubmit={handleSubmit}>
        {/* -- Identity ---------------------------------------------------- */}
        <div className="detail-section">
          <h2 className="section-title">Identity</h2>
          <div className="form-grid">
            
            {/* --- Genus Row --- */}
            <div className="form-field">
              <label className="form-label">{nomenclature.isIntergeneric ? "Mother Genus *" : "Genus *"}</label>
              <AutocompleteInput
                field="genus"
                value={nomenclature.baseGenus}
                onChange={(v) => handleNomenclatureChange({ baseGenus: v })}
                onSelect={(s) => {
                  handleNomenclatureChange({ baseGenus: s.value, baseSpecies: '' });
                  setSuggestedCommonNames([]);
                }}
                placeholder="e.g. Alocasia"
              />
            </div>

            {nomenclature.isIntergeneric && (
              <div className="form-field">
                <label className="form-label">Father Genus *</label>
                <input
                  type="text"
                  value={nomenclature.fatherGenus}
                  onChange={(e) => handleNomenclatureChange({ fatherGenus: e.target.value })}
                  className="form-input"
                  placeholder="e.g. Colocasia"
                />
              </div>
            )}

            {/* --- Species Row --- */}
            <div className="form-field">
              <label className="form-label">{nomenclature.isHybrid ? "Mother Species *" : "Species *"}</label>
              <AutocompleteInput
                field="species"
                value={nomenclature.baseSpecies}
                onChange={(v) => { handleNomenclatureChange({ baseSpecies: v }); setSuggestedCommonNames([]); }}
                onSelect={(s) => {
                  handleNomenclatureChange({ baseSpecies: s.value });
                  setSuggestedCommonNames([]);
                  const g = nomenclature.baseGenus.trim();
                  const sp = s.value.trim();
                  if (g && sp) {
                    fetch(`/species-care/suggested-names?genus=${encodeURIComponent(g)}&species=${encodeURIComponent(sp)}`)
                      .then(r => r.ok ? r.json() : { names: [] })
                      .then(d => setSuggestedCommonNames(d.names || []))
                      .catch(() => {});
                  }
                }}
                genusValue={nomenclature.baseGenus}
                placeholder="e.g. melo"
                disabled={nomenclature.speciesUnknown}
              />
              {nomenclature.speciesUnknown && (
                <small className="field-help">
                  Species is marked unknown / unresolved in Additional Naming Options.
                </small>
              )}
              {speciesCultivarHint && (
                <small className="field-hint">
                  Looks like a cultivar name — the dedicated <strong>Cultivar</strong> field keeps this cleaner.{' '}
                  <button type="button" className="hint-action-btn" onClick={handleMoveSpeciesToCultivar}>
                    Move to Cultivar field
                  </button>
                </small>
              )}
              {speciesTradeNameHint && (
                <small className="field-hint">
                  These look like English words, not a Latin species epithet. If this is a trade or marketing name, use the dedicated field — trade names usually don't have a known species.{' '}
                  <button type="button" className="hint-action-btn" onClick={handleMoveSpeciesToTradeName}>
                    Yes, treat as trade name
                  </button>
                </small>
              )}
            </div>

            {nomenclature.isHybrid && (
              <div className="form-field">
                <label className="form-label">Father Species *</label>
                <AutocompleteInput
                  field="species"
                  value={nomenclature.fatherSpecies}
                  onChange={(v) => handleNomenclatureChange({ fatherSpecies: v })}
                  onSelect={(s) => handleNomenclatureChange({ fatherSpecies: s.value })}
                  genusValue={nomenclature.isIntergeneric && nomenclature.fatherGenus ? nomenclature.fatherGenus : nomenclature.baseGenus}
                  placeholder="e.g. reginula"
                />
              </div>
            )}

            <div className="form-field">
              <label className="form-label">Common Name</label>
              <input
                type="text"
                name="common_name"
                value={form.common_name}
                onChange={handleChange}
                className="form-input"
                placeholder="Optional nickname"
              />
              {suggestedCommonNames.length > 0 && (
                <div className="common-name-suggestions">
                  <span className="common-name-suggestions-label">Suggested:</span>
                  {suggestedCommonNames.map(name => (
                    <button
                      key={name}
                      type="button"
                      className={`suggestion-chip${form.common_name === name ? ' suggestion-chip--active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, common_name: f.common_name === name ? '' : name }))}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
              {commonNameTradeHint && (
                <small className="field-hint">
                  If this is a trade or marketing name rather than a common name, you can also record it under <strong>Trade Name</strong> in Additional Naming Options.
                </small>
              )}
            </div>

            {!wishlistMode && (
            <div className="form-field">
              <label className="form-label">Status</label>
              <select name="status" value={form.status} onChange={handleChange} className="form-input">
                {statusMetadata.map(status => (
                  <option key={status.key} value={status.key}>{formatStatus(status.key, statusByKey)}</option>
                ))}
              </select>
            </div>
            )}
            {form.status === 'deceased' && lossReasonOptions.length > 0 && (
            <div className="form-field">
              <label className="form-label">Loss Reason</label>
              <select name="loss_reason" value={form.loss_reason} onChange={handleChange} className="form-input">
                <option value="">— Unknown —</option>
                {lossReasonOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            )}
            {(showDispositionPanel || showLoanPanel) && (
              <div className="form-field form-field-full">
                <div className="status-detail-panel">
                  <div className="status-detail-panel-header">
                    <span className={`status-badge status-${form.status}`}>{formatStatus(form.status, statusByKey)}</span>
                    <span className="field-hint">
                      {showLoanPanel
                        ? 'Loan details will be saved with this status change.'
                        : 'Archive details will be saved with this status change.'}
                    </span>
                  </div>

                  <div className="form-grid">
                    <div className="form-field">
                      <label className="form-label">
                        {showLoanPanel ? 'Loan Date' : form.status === 'sold' ? 'Sale Date' : form.status === 'swapped' ? 'Swap Date' : form.status === 'gifted' ? 'Gift Date' : 'Date'}
                      </label>
                      <input type="date" name="date" value={statusDetails.date} onChange={handleStatusDetailChange} className="form-input" />
                    </div>

                    {form.status === 'sold' && (
                      <div className="form-field">
                        <label className="form-label">Sale Price ($)</label>
                        <input type="number" name="amount" value={statusDetails.amount} onChange={handleStatusDetailChange} className="form-input" step="0.01" min="0" inputMode="decimal" />
                      </div>
                    )}

                    {showLoanPanel ? (
                      <>
                        <div className="form-field">
                          <label className="form-label">{form.status === 'loaned_in' ? 'Loaned From' : 'Loaned To'}</label>
                          <input name="person_name" value={statusDetails.person_name} onChange={handleStatusDetailChange} className="form-input" placeholder="Optional name" />
                        </div>
                        <div className="form-field">
                          <label className="form-label">Contact</label>
                          <input name="person_contact" value={statusDetails.person_contact} onChange={handleStatusDetailChange} className="form-input" placeholder="Optional contact" />
                        </div>
                        <div className="form-field">
                          <label className="form-label">Expected Return</label>
                          <input type="date" name="expected_return_at" value={statusDetails.expected_return_at} onChange={handleStatusDetailChange} className="form-input" />
                        </div>
                      </>
                    ) : form.status !== 'deceased' && (
                      <>
                        <div className="form-field">
                          <label className="form-label">{form.status === 'sold' ? 'Buyer' : form.status === 'gifted' ? 'Recipient' : 'Swapped With'}</label>
                          <input name="counterparty_name" value={statusDetails.counterparty_name} onChange={handleStatusDetailChange} className="form-input" placeholder="Optional name" />
                        </div>
                        <div className="form-field">
                          <label className="form-label">Contact</label>
                          <input name="counterparty_contact" value={statusDetails.counterparty_contact} onChange={handleStatusDetailChange} className="form-input" placeholder="Optional contact" />
                        </div>
                      </>
                    )}

                    {form.status === 'swapped' && (
                      <>
                        <div className="form-field">
                          <label className="form-label">Received Plant</label>
                          <input
                            value={swapSearch}
                            onChange={e => { setSwapSearch(e.target.value); setSelectedSwapPlant(null); }}
                            className="form-input"
                            placeholder="Search existing plant"
                          />
                          {selectedSwapPlant && (
                            <small className="field-hint">Selected {selectedSwapPlant.specimen_id}</small>
                          )}
                          {swapMatches.length > 0 && !selectedSwapPlant && (
                            <div className="duplicate-list status-search-results">
                              {swapMatches.slice(0, 5).map(match => (
                                <button
                                  key={match.id}
                                  type="button"
                                  className="duplicate-card"
                                  onClick={() => {
                                    setSelectedSwapPlant(match);
                                    setSwapSearch(`${match.specimen_id} · ${plantDisplayName(match)}`);
                                  }}
                                >
                                  <span className="duplicate-name">{plantDisplayName(match)}</span>
                                  <span className="duplicate-meta">{match.specimen_id}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="form-field">
                          <label className="form-label">Received Description</label>
                          <input name="received_description" value={statusDetails.received_description} onChange={handleStatusDetailChange} className="form-input" placeholder="Optional if not yet added" />
                        </div>
                      </>
                    )}

                    <div className="form-field form-field-full">
                      <label className="form-label">Status Notes</label>
                      <textarea name="notes" value={statusDetails.notes} onChange={handleStatusDetailChange} className="form-input" rows="2" placeholder="Optional context" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {isWishlistForm && (
            <div className="detail-section">
              <h2 className="section-title">Wishlist</h2>
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">Wishlist Priority</label>
                  <select
                    name="wishlist_priority"
                    value={form.wishlist_priority}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select priority</option>
                    <option value="1">1 — Top priority</option>
                    <option value="2">2 — High</option>
                    <option value="3">3 — Medium</option>
                    <option value="4">4 — Low</option>
                    <option value="5">5 — Someday</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Target Price ($)</label>
                  <input
                    type="number"
                    name="target_price"
                    value={form.target_price}
                    onChange={handleChange}
                    className="form-input"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    placeholder="Optional target price"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Shop / Seller</label>
                  <HistorySuggestInput
                    field="shop_name"
                    name="shop_name"
                    value={form.shop_name}
                    onChange={handleChange}
                    placeholder="Optional shop name"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Listing URL</label>
                  <input
                    type="url"
                    name="wishlist_url"
                    value={form.wishlist_url}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Optional listing link"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Acquisition Notes</label>
                  <textarea
                    name="acquisition_notes"
                    value={form.acquisition_notes}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Optional acquisition planning notes"
                    rows="2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* --- Additional Naming Options Toggle --- */}
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="naming-toggle-btn"
              onClick={() => setShowNamingOptions(!showNamingOptions)}
              aria-expanded={showNamingOptions}
            >
              <Icon name={showNamingOptions ? 'chevronDown' : 'chevronRight'} size={14} />
              {showNamingOptions ? 'Hide additional naming options' : 'Additional naming options (hybrids, cultivars, etc.)'}
            </button>

            {showNamingOptions && (
              <>
              <div className="nomenclature-options">
                <div className="nomenclature-option-toggles">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={nomenclature.isHybrid} 
                      onChange={(e) => handleNomenclatureChange({ isHybrid: e.target.checked })} 
                    />
                    <span>Interspecific Hybrid</span>
                  </label>
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={nomenclature.isIntergeneric} 
                      onChange={(e) => handleNomenclatureChange({ isIntergeneric: e.target.checked })} 
                    />
                    <span>Intergeneric Hybrid</span>
                  </label>
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={nomenclature.hasInfraspecific} 
                      onChange={(e) => handleNomenclatureChange({ hasInfraspecific: e.target.checked })} 
                    />
                    <span>Natural Rank (var/subsp)</span>
                  </label>
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={nomenclature.hasCultivar} 
                      onChange={(e) => handleNomenclatureChange({ hasCultivar: e.target.checked })} 
                    />
                    <span>Cultivar Name</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={nomenclature.hasTradeName}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        handleNomenclatureChange({ hasTradeName: checked });
                        if (!checked) setForm(prev => ({ ...prev, trade_name: '', id_basis: '' }));
                      }}
                    />
                    <span>Trade Name</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={nomenclature.speciesUnknown}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        handleNomenclatureChange({
                          speciesUnknown: checked,
                          baseSpecies: checked ? '' : nomenclature.baseSpecies,
                          fatherSpecies: checked ? '' : nomenclature.fatherSpecies,
                          isHybrid: checked ? false : nomenclature.isHybrid,
                          hasInfraspecific: checked ? false : nomenclature.hasInfraspecific,
                        });
                        if (checked) setForm(prev => ({ ...prev, id_basis: '' }));
                      }}
                    />
                    <span>Species unknown / unresolved</span>
                  </label>
                </div>

                <div className="form-grid nomenclature-fields-grid">
                  {nomenclature.hasTradeName && (
                    <div className="form-field">
                      <label className="form-label">Trade Name</label>
                      <input
                        type="text"
                        name="trade_name"
                        value={form.trade_name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g. Polly"
                      />
                      <small className="field-help">
                        Trade names are usually written in plain title case — not italicized and not in single quotes. Plants sold only under a trade name often have no known species; tick <em>Species unknown / unresolved</em> above if that applies.
                      </small>
                      {canUseTradeNameForId() && (
                        <label className="checkbox-label specimen-id-checkbox">
                          <input
                            type="checkbox"
                            checked={form.id_basis === 'trade_name'}
                            onChange={(e) => setForm(prev => ({
                              ...prev,
                              id_basis: e.target.checked ? 'trade_name' : '',
                            }))}
                          />
                          <span>Use trade name for Specimen ID</span>
                        </label>
                      )}
                    </div>
                  )}
                  {nomenclature.hasInfraspecific && (
                    <>
                      <div className="form-field">
                        <label className="form-label">Rank</label>
                        <select 
                          value={nomenclature.infraspecificRank} 
                          onChange={(e) => handleNomenclatureChange({ infraspecificRank: e.target.value })}
                          className="form-input"
                        >
                          <option value="var.">var. (Variety)</option>
                          <option value="subsp.">subsp. (Subspecies)</option>
                          <option value="f.">f. (Form)</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Rank Name</label>
                        <input 
                          type="text"
                          value={nomenclature.infraspecificName}
                          onChange={(e) => handleNomenclatureChange({ infraspecificName: e.target.value })}
                          className="form-input"
                          placeholder="e.g. sierrana"
                        />
                      </div>
                    </>
                  )}
                  {nomenclature.hasCultivar && (
                    <div className="form-field">
                      <label className="form-label">Cultivar Name</label>
                      <input 
                        type="text"
                        value={nomenclature.cultivar}
                        onChange={(e) => handleNomenclatureChange({ cultivar: e.target.value })}
                        className="form-input"
                        placeholder="e.g. Thai Constellation"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-field" style={{ marginTop: '1rem' }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  Specimen ID (Manual Override)
                  <button
                    type="button"
                    onClick={() => setShowSpecimenInfo(prev => !prev)}
                    aria-label="Show specimen ID formula"
                    title="Show specimen ID formula"
                    className="specimen-info-button"
                  >
                    i
                  </button>
                </label>
                <input
                  type="text"
                  name="specimen_id"
                  value={form.specimen_id || ''}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g. ALO-MEL-001"
                />
                <small className="field-help">
                  Leave blank to auto-generate. Each plant must have a unique Specimen ID.
                </small>
                {showSpecimenInfo && (
                  <div className="specimen-info-popover">
                    <p>Specimen ID format: GGG-XXX-###</p>
                    <p>GGG is the genus code. XXX is the identity code. ### is the next unique number in your collection.</p>
                    <p>Species example: ALO-MEL-001 for Alocasia melo.</p>
                    <p>Cultivar example: ALO-TCO-001 for Alocasia sp. 'Thai Constellation' (derived from cultivar name).</p>
                    <p>Hybrid example: ALO-MEL×REG-001 for a melo × reginula cross.</p>
                    <p>Trade name example: ALO-PLY-001 for Alocasia Polly.</p>
                    <p>Genus-only fallback (no species or cultivar): ALO-SPC-001.</p>
                    <p>Editing this field changes the plant's unique ID and reorganizes where its saved photos are stored.</p>
                  </div>
                )}
              </div>
              </>
            )}
          </div>

          <div className="specimen-preview" aria-live="polite">
            <div>
              <span className="form-label">Specimen ID Preview</span>
              <p className="field-help">Preview only. The final number is assigned when the plant is saved.</p>
            </div>
            <div className="specimen-preview-value">
              {preview.value ? (
                <>
                  <span>{preview.value}</span>
                  <small>{preview.mode}</small>
                </>
              ) : (
                <small>Enter identity fields to preview</small>
              )}
            </div>
          </div>
          

        </div>

        {!isWishlistForm && (
        <div className="detail-section">
          <h2 className="section-title">Acquisition</h2>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Acquisition Date</label>
              <input
                type="date"
                name="acquisition_date"
                value={form.acquisition_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-field">
              <label className="form-label">Source</label>
              <HistorySuggestInput
                field="acquisition_source"
                name="source"
                value={form.source}
                onChange={handleChange}
                placeholder="e.g. Local nursery, Etsy"
              />
            </div>
            <div className="form-field">
              <label className="form-label">Cost ($)</label>
              <input
                type="number"
                name="cost"
                value={form.cost}
                onChange={handleChange}
                className="form-input"
                step="0.01"
                min="0"
                inputMode="decimal"
                placeholder="0.00"
              />
            </div>
            <div className="form-field form-field-full">
              <label className="form-label">Acquisition Notes</label>
              <textarea
                name="acquisition_notes"
                value={form.acquisition_notes}
                onChange={handleChange}
                className="form-input"
                placeholder="Optional acquisition context"
                rows="2"
              />
            </div>
          </div>
        </div>
        )}

        {!isWishlistForm && (
        <div className="detail-section">
          <h2 className="section-title">Growing Conditions</h2>
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">
                Growing Method
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => setShowGrowingMethodInfo(prev => !prev)}
                    aria-label="Growing method info"
                    title="Growing method info"
                    className="specimen-info-button"
                  >
                    i
                  </button>
                )}
              </label>
              {isEditing && showGrowingMethodInfo && (
                <div className="specimen-info-popover">
                  <p>Correcting a data entry error? Update the field directly.</p>
                  <p>If you actually repotted or refreshed the substrate, log a <strong>Repot</strong> or <strong>Substrate Change</strong> observation instead — that keeps a dated record on the plant's timeline.</p>
                </div>
              )}
              <select name="growing_method" value={form.growing_method} onChange={(e) => {
                handleChange(e);
                setForm(prev => ({ ...prev, growing_method: e.target.value, substrate: '' }));
              }} className="form-input">
                <option value="">— Select —</option>
                {growingMethods.map(m => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </select>
            </div>
            {form.growing_method ? (
              <SubstratePicker
                key={form.growing_method}
                substrates={growingMethods.find(m => m.name === form.growing_method)?.substrates || []}
                value={form.substrate}
                onChange={v => setForm(prev => ({ ...prev, substrate: v }))}
                methodName={form.growing_method}
              />
            ) : (
              <div className="form-field">
                <label className="form-label">Substrate</label>
                <select className="form-input" disabled>
                  <option>Select method first</option>
                </select>
              </div>
            )}
            <div className="form-field">
              <label className="form-label">Moss Pole</label>
              <select name="moss_pole_type" value={form.moss_pole_type} onChange={handleChange} className="form-input">
                <option value="">None</option>
                {mossPoleOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label className="form-label">Cabinet ID</label>
              <input
                type="text"
                name="cabinet_id"
                value={form.cabinet_id}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. Shelf 2"
              />
            </div>
          </div>
        </div>
        )}

        {!isWishlistForm && (
        <div className="detail-section">
          <h2 className="section-title">Location</h2>
          <div className="checkbox-group">
            {locations.map(loc => (
              <label key={loc.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.location_ids.includes(loc.id)}
                  onChange={() => handleToggle('location_ids', loc.id)}
                />
                <span>{loc.name}</span>
              </label>
            ))}
          </div>
        </div>
        )}

        {!isWishlistForm && (
        <div className="detail-section">
          <h2 className="section-title">Light Source</h2>
          <div className="checkbox-group">
            {lightSources.map(ls => (
              <label key={ls.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.light_source_ids.includes(ls.id)}
                  onChange={() => handleToggle('light_source_ids', ls.id)}
                />
                <span>{ls.name}</span>
              </label>
            ))}
          </div>
        </div>
        )}

        {!isWishlistForm && (
        <>
        {/* -- Tags -------------------------------------------------------- */}
        <div className="detail-section">
          <h2 className="section-title">Tags</h2>
          <div className="checkbox-group">
            {tags.map(tag => (
              <label key={tag.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.tag_ids.includes(tag.id)}
                  onChange={() => handleToggle('tag_ids', tag.id)}
                />
                <span>{tag.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* -- Notes ------------------------------------------------------- */}
        <div className="detail-section">
          <h2 className="section-title">Notes</h2>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            className="form-input form-textarea"
            rows="3"
            placeholder="Any additional notes..."
          />
        </div>
        </>
        )}

        {/* -- Duplicate Detection --------------------------------------------- */}
        {formDuplicates && formDuplicates.length > 0 && (() => {
          const { genus: dupGenus, species: dupSpecies } = buildFinalNames();
          return (
          <div className="detail-section duplicate-panel">
            <h2 className="section-title">Plant Already in Collection</h2>
            <p className="duplicate-intro">
              You already have {formDuplicates.length} <em>{dupGenus} {dupSpecies}</em> in your collection.
            </p>

            <div className="duplicate-matches">
              {formDuplicates.map(d => (
                <div key={d.id} className="duplicate-match-wrapper">
                  <div className="duplicate-match">
                    {d.latest_photo_id ? (
                      <img
                        src={photoUrl(d.latest_photo_id, d.latest_photo_rotation_version, d.latest_photo_created_at)}
                        alt={plantDisplayName(d) || d.specimen_id}
                        className="duplicate-match-photo"
                        onClick={() => setExpandedDuplicate(expandedDuplicate === d.id ? null : d.id)}
                      />
                    ) : (
                      <div className="duplicate-match-photo-empty"><Icon name="sprout" size={24} /></div>
                    )}
                    <div className="duplicate-match-info">
                      <span className="duplicate-match-name">{plantDisplayName(d) || d.specimen_id}</span>
                      <span className="duplicate-match-id">{d.specimen_id}</span>
                      <span className={`status-badge status-${d.status}`}>{formatStatus(d.status, statusByKey)}</span>
                    </div>
                    <div className="duplicate-match-actions">
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpandedDuplicate(expandedDuplicate === d.id ? null : d.id)}>
                        {expandedDuplicate === d.id ? 'Collapse' : 'Details'}
                      </button>
                    </div>
                  </div>

                  {expandedDuplicate === d.id && (
                    <div className="duplicate-expanded">
                      {d.latest_photo_id && (
                        <img
                          src={photoUrl(d.latest_photo_id, d.latest_photo_rotation_version, d.latest_photo_created_at)}
                          alt={plantDisplayName(d) || d.specimen_id}
                          className="duplicate-expanded-photo"
                        />
                      )}
                      <div className="duplicate-expanded-details">
                        {d.acquisition_date && <span className="detail-tag">Acquired: {d.acquisition_date}</span>}
                        {d.growing_method && <span className="detail-tag">{d.growing_method}</span>}
                        {d.substrate && d.substrate !== d.growing_method && <span className="detail-tag">{formatSubstrate(d.substrate)}</span>}
                        {d.locations?.map((loc, i) => <span key={i} className="detail-tag location-tag">{loc}</span>)}
                        {d.last_observation_date && <span className="detail-tag">Last observation: {d.last_observation_date}</span>}
                        {d.photo_count > 0 && <span className="detail-tag"><Icon name="camera" size={12} style={{marginRight:3}} />{d.photo_count} photos</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="duplicate-actions">
              <button type="button" className="btn btn-primary" onClick={submitPlant}>
                Different specimen — create anyway
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setFormDuplicates(null)}>
                Cancel
              </button>
            </div>
          </div>
          );
        })()}

        {/* -- Actions ----------------------------------------------------- */}
        {confirmingDelete && (
          <div className="inline-confirm">
            <span className="inline-confirm-msg">Delete this plant and all its observations, measurements, and photos? This cannot be undone.</span>
            <div className="inline-confirm-actions">
              <button type="button" className="inline-confirm-no" onClick={() => setConfirmingDelete(false)}>Cancel</button>
              <button type="button" className="inline-confirm-yes" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        )}
        {confirmingIdChange && (
          <div className="inline-confirm">
            <span className="inline-confirm-msg">Changing the Specimen ID also reorganizes where this plant's saved photos are stored. Proceed?</span>
            <div className="inline-confirm-actions">
              <button type="button" className="inline-confirm-no" onClick={() => setConfirmingIdChange(false)}>Cancel</button>
              <button type="button" className="inline-confirm-yes inline-confirm-yes--neutral" onClick={handleSubmit}>Proceed</button>
            </div>
          </div>
        )}
        <div className="form-actions">
          {isEditing && (
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={confirmingDelete}>
              Delete Plant
            </button>
          )}
          <div className="form-actions-right">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={async () => {
                // If we came from the add flow, the plant was pre-created.
                // Delete it on cancel so we don't leave an orphan record.
                if (fromAddFlow && id) {
                  await fetch(`/plants/${id}`, { method: 'DELETE' }).catch(() => {});
                  // replace: orphan was deleted, no reason to return here via back
                  navigate('/new', { replace: true });
                } else {
                  navigate(backLink, backOpts);
                }
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Plant'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default PlantForm;
